import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Card,
  CardActions,
  CardContent,
  Collapse,
  Skeleton,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ExpandMore from '../../components/common/ExpandMore';
import language from '../../constants/leng';
import { IStakeholdersDetails } from '../../services/auth/types';
import { displayValue, displayPercentage } from '../../utils/formater';
import { selectSelectedLanguage } from '../store/language/selectors';

interface IStakeholders {
  stakeholder?: IStakeholdersDetails[];
  loading: boolean;
}

const StakeholdersDetails = ({ stakeholder, loading }: IStakeholders) => {
  const selectedLanguage = useSelector(selectSelectedLanguage);
  const [expandedItems, setExpandedItems] = useState<boolean[]>([]);
  const { t } = useTranslation();

  const handleExpandClick = (index: number) => {
    const newExpandedItems = [...expandedItems];
    newExpandedItems[index] = !newExpandedItems[index];
    setExpandedItems(newExpandedItems);
  };
  const documentTypeOptions: { key: string; value: string }[] = [
    { value: 'INE', key: t('INE') },
    { value: 'CURP', key: t('CURP') },
    { value: 'PASS', key: t('PssType') },
    { value: 'SSN', key: t('SSNType') },
    { value: 'CC', key: t('CCType') },
    { value: 'CE', key: t('CEType') },
    { value: 'NIT', key: t('NITType') },
    { value: 'PASS', key: t('PASSType') },
    { value: 'PEP', key: t('PEPType') },
  ];

  if (loading) {
    return <Skeleton variant="rectangular" height={100} />;
  }

  return (
    <Card
      className="my-4"
      title={language[selectedLanguage.language].stakeholdersDetails}
    >
      <CardContent>
        {stakeholder?.map((stake, index) => (
          <>
            <CardActions
              disableSpacing
              className="border-2 border-gray-200 mb-4"
            >
              <Typography variant="h6">
                {t('onbStHlSectionTitle', { index: index + 1 })}
              </Typography>
              <ExpandMore
                expand={expandedItems[index]}
                onClick={() => handleExpandClick(index)}
                aria-expanded={expandedItems[index]}
                aria-label="show more"
              >
                <ExpandMoreIcon />
              </ExpandMore>
            </CardActions>
            <Collapse in={expandedItems[index]} timeout="auto">
              <CardContent>
                <div className="flex flex-wrap justify-between">
                  <div className="w-1/2 md:w-1/4 mb-2">
                    <p className="text-xs text-label-light mb-4">
                      {t('onbStakeAddress1')}
                    </p>
                    <p className="text-sm">
                      {displayValue(stake?.address_line1)}
                    </p>
                  </div>
                  <div className="w-1/2 md:w-1/4 mb-2">
                    <p className="text-xs text-label-light mb-4">
                      {t('onbStakeAddress2')}
                    </p>
                    <p className="text-sm">
                      {displayValue(stake?.address_line2)}
                    </p>
                  </div>
                  <div className="w-1/2 md:w-1/4 mb-2">
                    <p className="text-xs text-label-light mb-4">
                      {t('onbStakeBirthDate')}
                    </p>
                    <p className="text-sm">{displayValue(stake?.birthDate)}</p>
                  </div>
                  <div className="w-1/2 md:w-1/4 mb-2">
                    <p className="text-xs text-label-light mb-4">
                      {t('onbStakeCitizenship')}
                    </p>
                    <p className="text-sm">
                      {displayValue(stake?.citizenship)}
                    </p>
                  </div>
                  <div className="w-1/2 md:w-1/4 mb-2">
                    <p className="text-xs text-label-light mb-4">
                      {t('onbStakeCity')}
                    </p>
                    <p className="text-sm">{displayValue(stake?.city)}</p>
                  </div>
                  <div className="w-1/2 md:w-1/4 mb-2">
                    <p className="text-xs text-label-light mb-4">
                      {t('onbStakeName')}
                    </p>
                    <p className="text-sm">{displayValue(stake?.name)}</p>
                  </div>
                  <div className="w-1/2 md:w-1/4 mb-2">
                    <p className="text-xs text-label-light mb-4">
                      {t('onbStakeNumberId')}
                    </p>
                    <p className="text-sm">{displayValue(stake?.numberId)}</p>
                  </div>
                  <div className="w-1/2 md:w-1/4 mb-2">
                    <p className="text-xs text-label-light mb-4">
                      {t('onbStakePercentage')}
                    </p>
                    <p className="text-sm">
                      {displayPercentage(stake?.percentage)}
                    </p>
                  </div>
                  <div className="w-1/2 md:w-1/4 mb-2">
                    <p className="text-xs text-label-light mb-4">
                      {t('onbStakeState')}
                    </p>
                    <p className="text-sm">{displayValue(stake?.state)}</p>
                  </div>
                  <div className="w-1/2 md:w-1/4 mb-2">
                    <p className="text-xs text-label-light mb-4">
                      {t('onbStakeTitle')}
                    </p>
                    <p className="text-sm">{displayValue(stake?.title)}</p>
                  </div>
                  <div className="w-1/2 md:w-1/4 mb-2">
                    <p className="text-xs text-label-light mb-4">
                      {t('onbStakeTypeDoc')}
                    </p>
                    <p className="text-sm">
                      {stake?.typeDocument ===
                      documentTypeOptions?.find(
                        (item) => item.value === stake?.typeDocument
                      )?.value
                        ? documentTypeOptions?.find(
                            (item) => item.value === stake?.typeDocument
                          )?.key
                        : 'ss'}
                    </p>
                  </div>
                  <div className="w-1/2 md:w-1/4 mb-2">
                    <p className="text-xs text-label-light mb-4" />
                    <p className="text-sm" />
                  </div>
                </div>
              </CardContent>
            </Collapse>
          </>
        ))}
      </CardContent>
    </Card>
  );
};
export default StakeholdersDetails;
